// Ported verbatim from nenda-autoplayer's src/lib/state/selectors.ts. Only
// imports changed: `Snapshot` from the now-ported ./autoplayerMachine
// (blocked on the machine existing, which is why this file waited),
// `adItemToMediaAsset` from the ported ../types/AdSlot.
import {
  ContentChannel,
  MediaAsset,
  Overlays,
  ScheduleType,
} from "../types/Schedule";
import { Snapshot } from "./autoplayerMachine";
import { adItemToMediaAsset } from "../types/AdSlot";

const DEFAULT_PLAYBACK_DURATION = 10;

export const selectSession = (snapshot: Snapshot) => snapshot.context.session;
export const selcectRedBeeSessionToken = (snapshot: Snapshot) =>
  snapshot.context.redBeeSessionToken;

export const selectCurrentContent = (snapshot: Snapshot) =>
  snapshot.context.playback.content.currentContent;

export const selectCurrentAssetIndex = (
  snapshot: Snapshot,
  type: ScheduleType
): number => {
  switch (type) {
    case ScheduleType.Content:
      return snapshot.context.playback.content.currentIndex;
    case ScheduleType.Ad:
      return snapshot.context.playback.ad.currentIndex !== undefined
        ? snapshot.context.playback.ad.currentIndex
        : -1;
    case ScheduleType.Signage:
      return snapshot.context.playback.signage.currentIndex !== undefined
        ? snapshot.context.playback.signage.currentIndex
        : -1;
    default:
      return -1;
  }
};

export const selectCurrentAsset = (
  snapshot: Snapshot
): MediaAsset | undefined => {
  const playbackType = selectPlaybackType(snapshot);

  switch (playbackType) {
    case ScheduleType.Content: {
      const currentContent =
        snapshot.context.playback.content.currentContent?.items[
          selectCurrentAssetIndex(snapshot, ScheduleType.Content)
        ];
      return currentContent;
    }
    case ScheduleType.Ad: {
      const index = selectCurrentAssetIndex(snapshot, ScheduleType.Ad);
      const adItem = snapshot.context.playback.ad.adslot?.ads[index];

      if (!adItem) {
        return undefined;
      }
      return adItemToMediaAsset(adItem);
    }
    case ScheduleType.Signage:
      return snapshot.context.playback.signage.currentSignagePlaylist[
        selectCurrentAssetIndex(snapshot, ScheduleType.Signage)
      ];
    default:
      return undefined;
  }
};

export const selectOverlays = (snapshot: Snapshot): Overlays | undefined => {
  const playbackType = selectPlaybackType(snapshot);
  if (playbackType !== ScheduleType.Content) return;

  if (!snapshot.context.playback.content.currentContent) return;

  const asContentChannel = snapshot.context.playback.content
    .currentContent as ContentChannel;

  const overlays = {
    logo: asContentChannel.logo,
    brandingOverlay: asContentChannel.brandingOverlay,
  };

  return overlays;
};

export const selectOverlayEquality = (
  prev: Overlays | undefined,
  next: Overlays | undefined
): boolean => {
  if (!prev && !next) return true;
  if (!prev || !next) return false;
  if (prev.logo?.locator !== next.logo?.locator) return false;
  if (prev.brandingOverlay?.locator !== next.brandingOverlay?.locator)
    return false;
  return true;
};

export const selectRemainingDuration = (snapshot: Snapshot) =>
  snapshot.context.playback.content.remainingDuration;

export const selectElapsedTime = (snapshot: Snapshot) => {
  const playbackType = selectPlaybackType(snapshot);
  if (playbackType === ScheduleType.Content) {
    return snapshot.context.playback.content.elapsedTime;
  }
  return 0;
};

export const selectIsContentPlaying = (snapshot: Snapshot) =>
  snapshot.matches({
    "Scheduled player": { Playback: ScheduleType.Content },
  });

export const selectPlaybackDuration = (snapshot: Snapshot): number => {
  const playbackType = selectPlaybackType(snapshot);
  const currentAsset = selectCurrentAsset(snapshot);

  if (!currentAsset) return 0;

  switch (playbackType) {
    case "Content": {
      const remainingDuration = selectRemainingDuration(snapshot);
      if (remainingDuration) {
        return remainingDuration;
      }
      return currentAsset.durationInSeconds || DEFAULT_PLAYBACK_DURATION;
    }
    case "Ad":
      return currentAsset.durationInSeconds;
    case "Signage":
      return currentAsset.durationInSeconds;
    default:
      return 0;
  }
};

export const selectIsAdPlaying = (snapshot: Snapshot) =>
  snapshot.matches({
    "Scheduled player": { Playback: ScheduleType.Ad },
  });

export const selectIsSignagePlaying = (snapshot: Snapshot) =>
  snapshot.matches({
    "Scheduled player": { Playback: ScheduleType.Signage },
  });

export const selectPlaybackType = (
  snapshot: Snapshot
): ScheduleType | "Unknown" => {
  if (selectIsAdPlaying(snapshot)) {
    return ScheduleType.Ad;
  } else if (selectIsContentPlaying(snapshot)) {
    return ScheduleType.Content;
  } else if (selectIsSignagePlaying(snapshot)) {
    return ScheduleType.Signage;
  }
  return "Unknown";
};

export const selectIsDebugView = (snapshot: Snapshot) => {
  return snapshot.matches({
    "Scheduled player": {
      "Debug view": "on",
    },
  });
};

export const selectIsSoundEnabled = (snapshot: Snapshot): boolean => {
  if (!snapshot.context.schedule) return false;
  if (!snapshot.context.schedule.options) return false;
  if (!snapshot.context.schedule.options.isSoundEnabled) return false;

  const playbackType = selectPlaybackType(snapshot);
  switch (playbackType) {
    case ScheduleType.Content:
      // Honor the per-content sound flag in addition to the schedule-level one
      // (the schedule-level flag is already guaranteed true above). A content
      // channel individually muted must stay muted even when the schedule
      // allows sound. Fall back to enabled when no current content is set.
      return (
        snapshot.context.playback.content.currentContent?.isSoundEnabled ?? true
      );
    case ScheduleType.Ad:
      return snapshot.context.schedule.options.isSoundEnabled;
    case ScheduleType.Signage:
      return snapshot.context.schedule.options.isSoundEnabled;
    default:
      return false;
  }
};

export const selectIsPlaybackCircuitBreakerOpen = (
  snapshot: Snapshot
): boolean => {
  return snapshot.context.playback.circuitBreakerState === "open";
};

/** Returns the next asset in the current playlist (wraps around). */
export const selectNextAsset = (snapshot: Snapshot): MediaAsset | undefined => {
  const playbackType = selectPlaybackType(snapshot);
  switch (playbackType) {
    case ScheduleType.Content: {
      const items = snapshot.context.playback.content.currentContent?.items;
      if (!items || items.length < 2) return undefined;
      const next =
        (selectCurrentAssetIndex(snapshot, ScheduleType.Content) + 1) %
        items.length;
      return items[next];
    }
    case ScheduleType.Ad: {
      const ads = snapshot.context.playback.ad.adslot?.ads;
      if (!ads || ads.length < 2) return undefined;
      const next =
        (selectCurrentAssetIndex(snapshot, ScheduleType.Ad) + 1) % ads.length;
      return adItemToMediaAsset(ads[next]!);
    }
    case ScheduleType.Signage: {
      const items = snapshot.context.playback.signage.currentSignagePlaylist;
      if (!items || items.length < 2) return undefined;
      const next =
        (selectCurrentAssetIndex(snapshot, ScheduleType.Signage) + 1) %
        items.length;
      return items[next];
    }
    default:
      return undefined;
  }
};
